import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  Injector,
} from '@angular/core';
import { Column, Editor, EditorArguments, EditorValidationResult } from 'angular-slickgrid';
import { LocalizationService } from '@abp/ng.core';
import { ZnSingleSelect } from '../../zn-select/zn-single-select';

type ZnSelectEditorOptions = {
  injector?: Injector;
  optionsLoader?: (params?: any) => any;
  errorEntityKey?: string;
  required?: boolean;
  validator?: (value: any, args?: any) => EditorValidationResult;
};

export class ZnSelectEditor implements Editor {
  private localizationService?: LocalizationService;
  private args: EditorArguments;
  private componentRef?: ComponentRef<ZnSingleSelect>;
  private appRef?: ApplicationRef;
  private envInjector?: EnvironmentInjector;
  private hostElement?: HTMLElement;
  private selectionSubscription?: { unsubscribe: () => void };
  private tabNavigationSubscription?: { unsubscribe: () => void };
  private originalValue: any;
  private currentValue: any;
  private originalValueType?: string;
  private currentLabel?: string;

  constructor(args: EditorArguments) {
    this.args = args;
    this.init();
  }

  init(): void {
    const container = this.args.container as HTMLElement;
    const options = this.getEditorOptions();
    const injector = options.injector;
    if (!injector) {
      return;
    }

    this.appRef = injector.get(ApplicationRef);
    this.envInjector = injector.get(EnvironmentInjector);
    this.localizationService = injector.get(LocalizationService);

    this.hostElement = document.createElement('div');
    this.hostElement.style.width = '100%';
    this.hostElement.style.height = '100%';
    this.hostElement.classList.add('zn-select-editor-host');
    container.appendChild(this.hostElement);

    this.componentRef = createComponent(ZnSingleSelect, {
      environmentInjector: this.envInjector,
      hostElement: this.hostElement,
    });
    this.appRef.attachView(this.componentRef.hostView);
    this.componentRef.setInput('tabNavigationEnabled', true);
    this.componentRef.changeDetectorRef.detectChanges();

    if (options.optionsLoader) {
      this.componentRef.setInput('optionsLoader', options.optionsLoader);
    }
    if (options.errorEntityKey) {
      this.componentRef.setInput('errorEntityKey', options.errorEntityKey);
    }
    this.selectionSubscription = this.componentRef.instance.selectedChange.subscribe(value => {
      this.currentValue = value;
      this.currentLabel = this.resolveCurrentLabel(value);
      this.commitEdit();
    });
    this.tabNavigationSubscription = this.componentRef.instance.tabNavigation.subscribe(shiftKey => {
      const grid = (this.args as any).grid;
      if (!grid) {
        return;
      }

      if (shiftKey) {
        grid.navigatePrev?.();
        return;
      }

      grid.navigateNext?.();
    });
    this.componentRef.instance.open();
  }

  destroy(): void {
    this.selectionSubscription?.unsubscribe();
    this.selectionSubscription = undefined;
    this.tabNavigationSubscription?.unsubscribe();
    this.tabNavigationSubscription = undefined;

    if (this.componentRef) {
      this.appRef?.detachView(this.componentRef.hostView);
      this.componentRef.destroy();
      this.componentRef = undefined;
    }

    if (this.hostElement) {
      this.hostElement.remove();
      this.hostElement = undefined;
    }
  }

  focus(): void {
    // No-op: zn-select handles focus via click.
  }

  loadValue(item: any): void {
    const column = this.args.column as Column;
    const fieldName = column.field || '';
    const value = item[fieldName];

    this.originalValue = value;
    this.currentValue = value;
    this.originalValueType = value === null || value === undefined ? undefined : typeof value;

    const displayNestedPath = column.params?.displayNestedPath as string | undefined;
    if (displayNestedPath && value !== null && value !== undefined && this.componentRef) {
      const label = displayNestedPath
        .split('.')
        .reduce((cur: any, part: string) => cur?.[part], item);
      if (label !== null && label !== undefined) {
        this.componentRef.setInput('options', [
          { key: String(value), value: String(label) }
        ]);
      }
    }

    this.componentRef?.instance.writeValue(value ?? null);
  }

  serializeValue(): any {
    return this.coerceValue(this.currentValue);
  }

  applyValue(item: any, state: any): void {
    const column = this.args.column as Column;
    const fieldName = column.field || '';
    item[fieldName] = this.coerceValue(state);

    const displayNestedPath = column.params?.displayNestedPath as string | undefined;
    if (displayNestedPath) {
      const labelToSet = this.currentLabel ?? null;
      this.setNestedValue(item, displayNestedPath, labelToSet);
    }
  }

  isValueChanged(): boolean {
    const coerced = this.coerceValue(this.currentValue);
    const original = this.originalValue;
    if (original === null || original === undefined) {
      return !(coerced === null || coerced === undefined || coerced === '');
    }
    return coerced !== original;
  }

  validate(): EditorValidationResult {
    const editorOptions = this.getEditorOptions();
    const value = this.currentValue;
    const column = this.args.column as Column;
    const fieldName = column.field || '';

    if (editorOptions.required && (value === null || value === undefined || value === '')) {
      return {
        valid: false,
        msg: this.localizationService.instant('LanguageService::DefaultErrorRequired'),
      };
    }

    if (editorOptions.validator) {
      const item = this.args.item || {};
      const updatedItem = { ...item, [fieldName]: value };
      const validationArgs = { ...this.args, item: updatedItem };
      return editorOptions.validator(value, validationArgs);
    }

    return { valid: true, msg: '' };
  }

  private getEditorOptions(): ZnSelectEditorOptions {
    const column = this.args.column as Column;
    return (column.editor as ZnSelectEditorOptions) || {};
  }

  private coerceValue(value: any): any {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (this.originalValueType === 'number') {
      const asNumber = Number(value);
      return Number.isNaN(asNumber) ? value : asNumber;
    }

    return value;
  }

  private setNestedValue(target: any, path: string, value: any): void {
    if (!target) return;

    const parts = path.split('.');
    let current = target;

    for (let i = 0; i < parts.length - 1; i += 1) {
      const key = parts[i];
      if (current[key] === null || current[key] === undefined) {
        current[key] = {};
      }
      current = current[key];
    }

    current[parts[parts.length - 1]] = value;
  }

  private commitEdit(): void {
    const grid = (this.args as any).grid;
    const editorLock = grid?.getEditorLock?.();
    if (!editorLock) return;

    editorLock.commitCurrentEdit?.();
  }

  private resolveCurrentLabel(value: any): string | undefined {
    if (!this.componentRef || value === null || value === undefined || value === '') {
      return undefined;
    }

    const singleId = Array.isArray(value) ? value[0] : value;
    if (singleId === null || singleId === undefined || singleId === '') {
      return undefined;
    }

    const component = this.componentRef.instance as any;
    if (typeof component.getLabelForId === 'function') {
      const label = component.getLabelForId(String(singleId));
      if (label !== null && label !== undefined && label !== '') {
        return String(label);
      }
    }

    if (typeof component.selectedLabels === 'function') {
      const labels = component.selectedLabels();
      if (Array.isArray(labels) && labels.length > 0) {
        return String(labels[0]);
      }
    }

    return undefined;
  }
}
